// penWidthSlider — 旧 Java applet `PaintToolCanvas.java:792-816` (mouseDragged) +
// `:498-509` (paint) のロジックを TS port した pure helper.
//
// 旧版踏襲のレイアウト確定値 (出典: PaintToolCanvas.java:498-509):
//   バー外枠: x=7, y=230, w=32, h=4 (旧版は左サイドバー 50px に対し 32px 幅)
//   ドラッグ式値変換: w = (x - 7) / 2  (x=7..39 → w=0..16)
//                    x < 7   → w = 1 (左外で 1 にスナップ)
//                    x > 39  → w = 16 (右外で 16 にスナップ)
//
// 新版は左サイドバー 140px に伸ばして使えるので、バー幅を引数で受け取り
// 比例計算で 0..MAX_PEN_WIDTH にマップする. 端で止まる問題を構造的に
// 起こさないため `xToPenWidth` 内で必ず clamp する (R10 と同じ思想).
//
// **2026-05-09 ペン最大値拡大 (consult 確定, architecture.md §16 / §19.15)**:
// - 新版デフォルトキャンバスは 1500² (旧版 800×450 の 6.25× 面積、線形比 2.5×)
// - 旧 `MAX_PEN_WIDTH = 16` は画面比で細すぎ、`drawWidths = [1,2,3,4,8,16]` も
//   実機で運用に乗らない (実測 by 作者本人, 2026-05-09)
// - 新版は **`MAX_PEN_WIDTH = 60`** (実機調整余地あり、薄ければ 80 まで拡大検討)
// - 6 プリセット **`[1, 3, 5, 10, 20, 40]`** に振り直し (細→太の対数寄り間隔で
//   旧 `[1, 2, 3, 4, 8, 16]` の刻み感を保ちつつ最大値を 40 に持ち上げる).
// - protocol 側 `MAX_STROKE_WIDTH = 64` の範囲内に収まるので protocol 改変不要.
// - 筆圧 ON 時の挙動は CSP 流相対スケール (§19.15): UI で選んだ太さは「筆圧最大時
//   の太さ」として機能、`min` を下回らない範囲でスケーリングされる.
//
// (architecture.md §16, §19.15, classic-ui-restore.md §4 R11)

import { valueToX, xToValue } from './legacyGaugeSlider.js';

/**
 * ペン太さの上限値. 新版 1500² キャンバス向けに旧版 16 → 60 に拡大
 * (architecture.md §16 / consult 2026-05-09).
 *
 * protocol の `MAX_STROKE_WIDTH = 64` の範囲内で運用する. 実機で薄ければ
 * 80 まで拡大検討する余地はあるが、その場合は protocol 側も上限拡大が要る.
 */
export const MAX_PEN_WIDTH = 60;

/**
 * 6 プリセットの初期値. 旧版 `[1, 2, 3, 4, 8, 16]` を新スケール (max 60) に
 * 振り直し: 旧版の「細→太の対数寄り間隔」を保ちつつ最大値を 40 に持ち上げる.
 *
 * 値の選択根拠:
 * - 細端 1px は旧版踏襲 (= ピクセルアート用の最細値, 残しておかないと 1px で
 *   描けない退化になる)
 * - 中間 3, 5, 10 は旧版 2, 3, 4, 8 を 1500² キャンバス比で間引きつつ伸ばし
 * - 太端 40 は MAX 60 に対し ~2/3, 旧版 16/27 の比率を踏襲 (旧版でもプリセット
 *   最大は MAX より少し下に置かれていた)
 * - 20 は 10 と 40 の log 中点 (≒ √(10*40)=20)
 */
export const DEFAULT_PEN_WIDTHS: ReadonlyArray<number> = [1, 3, 5, 10, 20, 40];

/**
 * 消しゴム専用プリセット (2026-05-27 consult 確定).
 *
 * 最大 = 60 (= MAX_PEN_WIDTH)、デフォルト選択 = 末尾(60)セル。描画用
 * `DEFAULT_PEN_WIDTHS` ([1,3,5,10,20,40]) とは別物として持つ.
 *
 * 経緯: 消しゴムは「広い面を一気に消したい」用途が大半で、描画ペンと同じ初期
 * 細端 (1px) では実用に合わない. 消しゴムバケット選択時はこの専用プリセットに
 * 差し替え、初期状態で最大(60)が選択された状態にする (= 開いてすぐ太く消せる).
 * 細端も 2px から並べて細かい消し込みにも対応する. 描画ペン (brush/flat/marker)
 * の挙動・初期値 (DEFAULT_PEN_WIDTHS / 選択 index 0 = 値 1) は一切変えない.
 */
export const DEFAULT_ERASER_PEN_WIDTHS: ReadonlyArray<number> = [2, 5, 10, 20, 40, 60];

/**
 * ペン幅 cell に描画する黒丸 (●) の最大直径 (px). cell 高さ `h-6` = 24px の
 * **80%** に相当 — 旧 Java 版 `PaintToolCanvas.java` の cell 25×20px / ● 最大
 * 16px (= 16/20 = 80%) と同じ比率を踏襲してノスタルジーを保つ
 * (consult 2026-05-09).
 *
 * `MAX_PEN_WIDTH = 60` まで値が伸びても ● は 20px で頭打ちにし、超える分は
 * cell 内側に数値併記で表現する (= `getPenWidthCellDotDisplay` 参照).
 */
export const PEN_WIDTH_CELL_DOT_MAX = 20;

/**
 * ペン幅 cell の最小 dot 直径 (px). `w = 0..1` の極細値でも視認できるよう
 * 2px の floor を入れる (旧来の `Math.max(2, w)` 挙動踏襲).
 */
export const PEN_WIDTH_CELL_DOT_MIN = 2;

/**
 * ペン幅 cell の表示パラメータ. ToolPanel.tsx の cell 描画で使う.
 */
export interface PenWidthCellDotDisplay {
  /** 黒丸 ● の直径 (px). 2..PEN_WIDTH_CELL_DOT_MAX で clamp. */
  readonly dotPx: number;
  /**
   * cell 内側に数値を併記するか. `w > PEN_WIDTH_CELL_DOT_MAX` のときだけ true
   * (= プリセット 1/3/5/10/20 までは ● だけで大小比較できるので非表示、21..60
   * は数値併記で識別する。consult 2026-05-09).
   */
  readonly showLabel: boolean;
  /** 表示する数値 (= `w` そのもの). `showLabel === true` のときに使う. */
  readonly label: number;
}

/**
 * ペン幅 `w` から cell 表示パラメータを算出する. dot は 2..20px で clamp、
 * `w > 20` (= プリセット 40 / 微調整スライダで 21+ を選んだ場合) は ● 内側に
 * 数値を併記する (consult 2026-05-09).
 *
 * @param w 現在のペン幅 (0..MAX_PEN_WIDTH 想定だが defensive に範囲外も clamp).
 */
export const getPenWidthCellDotDisplay = (w: number): PenWidthCellDotDisplay => {
  const clamped = Math.max(PEN_WIDTH_CELL_DOT_MIN, Math.min(PEN_WIDTH_CELL_DOT_MAX, w));
  return {
    dotPx: clamped,
    showLabel: w > PEN_WIDTH_CELL_DOT_MAX,
    label: w,
  };
};

/**
 * Canvas (or HTML 要素) 上の x 座標 → ペン幅 0..MAX_PEN_WIDTH.
 *
 * 2026-05-09: 内部実装は `legacyGaugeSlider.xToValue` の汎用 helper に委譲
 * (= 筆圧 min スライダ等で同じ計算を再利用). 振る舞い (整数 round, 範囲外
 * clamp) は完全互換.
 *
 * @param xCanvas pointer の x 座標 (バー左端を 0 とした px).
 * @param barWidth バーの描画幅 (px). xCanvas / barWidth で 0..1 に正規化される.
 * @returns 0..MAX_PEN_WIDTH の整数. 範囲外は端で clamp.
 */
export const xToPenWidth = (xCanvas: number, barWidth: number): number =>
  xToValue(xCanvas, barWidth, 0, MAX_PEN_WIDTH, 1);

/**
 * 与えられた幅 `w` がバー上のどの x 座標に対応するか. 描画 (ゲージ右端) で使う.
 *
 * 2026-05-09: 内部実装は `legacyGaugeSlider.valueToX` に委譲.
 *
 * @returns 0..barWidth の x 座標 (px).
 */
export const penWidthToX = (w: number, barWidth: number): number =>
  valueToX(w, barWidth, 0, MAX_PEN_WIDTH);

/**
 * 6 cell の 2x3 配置で「列優先」(`ix = index/3, iy = index%3`) インデックスを
 * グリッド (col, row) に変換する.
 *
 * 旧版 `PaintToolCanvas.drawPenWidthCursor` (line 674-702) の `ix = index/3,
 * iy = index%3` を踏襲. 6 cell の並び:
 *   index 0 → (col 0, row 0)  index 3 → (col 1, row 0)
 *   index 1 → (col 0, row 1)  index 4 → (col 1, row 1)
 *   index 2 → (col 0, row 2)  index 5 → (col 1, row 2)
 */
export const penWidthIndexToCell = (index: number): { col: 0 | 1; row: 0 | 1 | 2 } => {
  const col = (Math.floor(index / 3) % 2) as 0 | 1;
  const row = (index % 3) as 0 | 1 | 2;
  return { col, row };
};

/**
 * ペン幅 cell の見かけの順序 (top→bottom, left→right) で 6 個分を返す.
 * 列優先配置を CSS Grid (`grid-flow-col grid-rows-3`) で render する用.
 *
 * 戻り値は元配列の index. 例: [0, 1, 2, 3, 4, 5] のままだが, 配置を変える際の
 * 抽象として残しておく.
 */
export const PEN_WIDTH_CELL_ORDER: ReadonlyArray<number> = [0, 1, 2, 3, 4, 5];

/**
 * R11-fix: ペン幅 6 cell + 選択中 cell index の同時 state.
 *
 * 旧版 `PaintToolCanvas.java:91` の `selPenWidth` (cell index) と `penWidths`
 * (各 cell の値) を完全分離した設計を pure data として表現する.
 */
export interface PenWidthState {
  readonly widths: ReadonlyArray<number>;
  readonly selectedIndex: number;
}

/**
 * cell click: 選択 cell を更新する. widths は触らない.
 *
 * 範囲外 index は no-op (現状維持). 不正 input でも state を破壊しない.
 */
export const clickPenWidthCell = (state: PenWidthState, index: number): PenWidthState => {
  if (index < 0 || index >= state.widths.length) return state;
  return { ...state, selectedIndex: index };
};

/**
 * slider drag: 選択中 cell の **値だけ** を上書きする. selectedIndex は触らない.
 *
 * これが旧版 `mouseDragged` 挙動の核心: スライダーをどう動かしても選択中 cell の
 * 位置は移動しない (= bug #5978b3f の修正).
 *
 * @param newWidth 0..MAX_PEN_WIDTH. 範囲外は呼び出し側 (`xToPenWidth`) が clamp 済.
 */
export const setSelectedPenWidthValue = (state: PenWidthState, newWidth: number): PenWidthState => {
  const i = state.selectedIndex;
  if (i < 0 || i >= state.widths.length) return state;
  const next = state.widths.slice();
  next[i] = newWidth;
  return { ...state, widths: next };
};
